import React, { useEffect, useMemo, useState } from 'react';

import { getCategoryTree, getProductsInCategory } from '../../services/categoriesApi';

const TreeNode = ({ node, depth, onSelect, selectedCode }) => {
  const [expanded, setExpanded] = useState(depth < 1);
  const hasChildren = node.children?.length > 0;
  const isSelected = selectedCode === node.code;

  return (
    <li
      style={{
        marginLeft: depth === 0 ? 0 : 12,
        paddingLeft: 4,
        borderLeft: depth === 0 ? 'none' : '1px solid #2a3a52',
      }}
    >
      <div style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '2px 0' }}>
        <button
          type="button"
          onClick={() => setExpanded((e) => !e)}
          disabled={!hasChildren}
          style={{
            width: 16,
            background: 'transparent',
            border: 'none',
            color: hasChildren ? '#9bb' : '#445',
            cursor: hasChildren ? 'pointer' : 'default',
            padding: 0,
          }}
          aria-label={expanded ? 'collapse' : 'expand'}
        >
          {hasChildren ? (expanded ? '▾' : '▸') : '·'}
        </button>
        <button
          type="button"
          onClick={() => onSelect(node)}
          style={{
            background: isSelected ? '#1f3a5a' : 'transparent',
            border: '1px solid transparent',
            color: '#dde',
            cursor: 'pointer',
            padding: '2px 6px',
            borderRadius: 3,
            textAlign: 'left',
            flex: 1,
          }}
        >
          <span style={{ marginRight: 6 }}>{node.label}</span>
          <code style={{ color: '#7aa', fontSize: '0.85em' }}>{node.code}</code>
          {node.is_holding_area && (
            <span className="status" style={{ marginLeft: 6 }}>holding</span>
          )}
          <span className="muted" style={{ marginLeft: 8 }}>
            {(node.product_count || 0).toLocaleString()} SKU
            {node.product_count === 1 ? '' : 's'}
          </span>
        </button>
      </div>
      {expanded && hasChildren && (
        <ul style={{ listStyle: 'none', padding: 0, margin: 0 }}>
          {node.children.map((c) => (
            <TreeNode
              key={c.code}
              node={c}
              depth={depth + 1}
              onSelect={onSelect}
              selectedCode={selectedCode}
            />
          ))}
        </ul>
      )}
    </li>
  );
};

const CategoryTreeBrowser = () => {
  const [tree, setTree] = useState(null);
  const [error, setError] = useState(null);
  const [selected, setSelected] = useState(null);
  const [products, setProducts] = useState(null);
  const [productsLoading, setProductsLoading] = useState(false);

  useEffect(() => {
    let cancelled = false;
    getCategoryTree()
      .then((t) => {
        if (!cancelled) setTree(t);
      })
      .catch((e) => {
        if (!cancelled) setError(e.response?.data?.detail || e.message);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    if (!selected) {
      setProducts(null);
      return;
    }
    setProductsLoading(true);
    setProducts(null);
    let cancelled = false;
    getProductsInCategory(selected.code, { limit: 50 })
      .then((d) => {
        if (!cancelled) setProducts(d);
      })
      .catch((e) => {
        if (!cancelled) setError(e.response?.data?.detail || e.message);
      })
      .finally(() => {
        if (!cancelled) setProductsLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [selected]);

  const summary = useMemo(() => {
    if (!tree) return null;
    return tree.map((n) => `${n.label} (${n.product_count || 0})`).join(' · ');
  }, [tree]);

  if (error) return <div className="error">Error: {error}</div>;
  if (!tree) return <div>Loading tree…</div>;

  return (
    <div className="sync-dashboard">
      <header>
        <h1>Category tree browser</h1>
        <span className="muted">{summary}</span>
      </header>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
        <section className="card" style={{ overflow: 'auto' }}>
          <h2>Waterco tree</h2>
          <ul style={{ listStyle: 'none', padding: 0, margin: 0 }}>
            {tree.map((n) => (
              <TreeNode
                key={n.code}
                node={n}
                depth={0}
                onSelect={setSelected}
                selectedCode={selected?.code}
              />
            ))}
          </ul>
        </section>

        <section className="card">
          <h2>{selected ? `Products in ${selected.label}` : 'Select a category'}</h2>
          {!selected && (
            <p className="muted">
              Click a node on the left to load the first 50 SKUs that have that code in
              <code> akeneo_categories</code>.
            </p>
          )}
          {selected && productsLoading && <p>Loading SKUs…</p>}
          {selected && products && (
            <>
              <p className="muted">
                {products.total.toLocaleString()} total; showing{' '}
                {Math.min(products.total, products.limit)}.
              </p>
              <table>
                <thead>
                  <tr>
                    <th>SKU</th>
                    <th>Name</th>
                    <th>Brand</th>
                    <th>Type</th>
                  </tr>
                </thead>
                <tbody>
                  {products.items.map((p) => (
                    <tr key={p.sku}>
                      <td><code>{p.sku}</code></td>
                      <td>{p.product_name || p.name}</td>
                      <td>{p.brand}</td>
                      <td>{(p.product_type || []).join(', ') || <span className="muted">—</span>}</td>
                    </tr>
                  ))}
                  {products.items.length === 0 && (
                    <tr><td colSpan={4} className="muted">No SKUs assigned to this category.</td></tr>
                  )}
                </tbody>
              </table>
            </>
          )}
        </section>
      </div>
    </div>
  );
};

export default CategoryTreeBrowser;
